import { useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  InputAdornment, LinearProgress, Snackbar, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { useCountries } from "./useCountries";
import type { AdminCountry, CountryPatch } from "./types";
import { errorMessage } from "../../apiService/Core/countriesApi";
import AddCountryDialog from "./AddCountryDialog";
import StatesDialog from "./StatesDialog";
import PushPageHeader from "../../Components/Push/PushPageHeader";
import PushPanel from "../../Components/Push/PushPanel";
import { InlineAlert } from "../../Components/Push/PushDisabledNotice";
import {
  dialogPaperSx, dialogTitleSx, headerBtnSx, outlineBtnSx, searchFieldStyle, tableSx, tdStyle, thStyle, trHover,
} from "../../Components/Push/pushStyles";

type Flag = "isActive" | "signupEnabled" | "onboardingEnabled" | "stateRequired";

interface Confirm {
  title: string;
  body: string;
  confirmLabel: string;
  run: () => Promise<void>;
}

export default function CountriesPage() {
  const { items, source, enforced, loading, loadError, busy, reload, patch, create, saveDefaults } = useCountries();
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<AdminCountry | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" } | null>(null);
  const [savingDefaults, setSavingDefaults] = useState(false);

  const readOnly = source === "fallback";

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((c) => c.name.toLowerCase().includes(q) || c.alpha2.toLowerCase() === q) : items;
  }, [items, query]);

  const nextSortOrder = (items.reduce((m, c) => Math.max(m, c.sortOrder), 0) || 0) + 10;

  const savePatch = async (alpha2: string, change: CountryPatch, okMsg = "Saved") => {
    try {
      await patch(alpha2, change);
      setToast({ msg: okMsg, severity: "success" });
    } catch (e) {
      setToast({ msg: errorMessage(e), severity: "error" });
      throw e;
    }
  };

  const onToggle = (c: AdminCountry, flag: Flag, value: boolean) => {
    const run = async () => { await savePatch(c.alpha2, { [flag]: value } as CountryPatch).catch(() => undefined); };

    if (flag === "isActive" && !value) {
      setConfirm({
        title: `Turn off ${c.name}?`,
        body: "It disappears from signup and from the country-of-residence list. Existing users in this country are not affected.",
        confirmLabel: "Turn off", run,
      });
    } else if (flag === "onboardingEnabled" && value) {
      setConfirm({
        title: `Allow onboarding in ${c.name}?`,
        body: "Users will be able to choose this country as their residence. Make sure KYC level, currency, loan provider and fee setup exist for it first.",
        confirmLabel: "Allow onboarding", run,
      });
    } else {
      void run();
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirming(true);
    try { await confirm.run(); } finally { setConfirming(false); setConfirm(null); }
  };

  const onSaveDefaults = async () => {
    setSavingDefaults(true);
    try {
      await saveDefaults();
      setToast({ msg: "Defaults saved to the database", severity: "success" });
    } catch (e) {
      setToast({ msg: errorMessage(e), severity: "error" });
    } finally {
      setSavingDefaults(false);
    }
  };

  const sw = (c: AdminCountry, flag: Flag, label: string, extraDisabled = false, hint?: string) => {
    const el = (
      <span>
        <Switch
          size="small"
          checked={c[flag]}
          disabled={readOnly || !!busy[c.alpha2] || extraDisabled}
          onChange={(_, v) => onToggle(c, flag, v)}
          inputProps={{ "aria-label": `${label} for ${c.name}` }}
        />
      </span>
    );
    return hint ? <Tooltip title={hint}>{el}</Tooltip> : el;
  };

  return (
    <Box className="common-layout flex-column">
      <PushPageHeader
        title="Countries and states"
        subtitle={
          <Typography fontSize={11} color="rgba(255,255,255,0.65)" mt={0.4}>
            Manage the countries and states available during signup and onboarding.
          </Typography>
        }
        actions={
          <>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={reload} sx={headerBtnSx}>
              Refresh
            </Button>
            <Button variant="outlined" onClick={() => setAddOpen(true)} disabled={readOnly || loading} sx={headerBtnSx}>
              Add country
            </Button>
          </>
        }
      />

      <PushPanel
        title="Country settings"
        icon={<PublicOutlinedIcon sx={{ color: "#ff6a3d", fontSize: 20 }} />}
        sx={{ mt: 2 }}
        bodySx={{ p: 0 }}
        action={
          <TextField
            size="small"
            placeholder="Search countries"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ ...searchFieldStyle, width: { xs: "100%", sm: 260 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon className="color-primary" /></InputAdornment> } }}
          />
        }
      >
        <Stack spacing={1.5} sx={{ p: 2 }}>
          {!enforced && !loading && !loadError && (
            <InlineAlert severity="info">
              Enforcement is off on the server. Unsupported countries are logged, not blocked, but these settings still control the country lists shown in the apps.
            </InlineAlert>
          )}
          {readOnly && !loading && (
            <InlineAlert severity="warning">
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" gap={1}>
                <span>Nothing is saved in the database yet. The apps are using the built-in list shown here. Save it to start editing.</span>
                <Button size="small" variant="outlined" onClick={onSaveDefaults} disabled={savingDefaults} sx={outlineBtnSx}>
                  {savingDefaults ? "Saving…" : "Save defaults"}
                </Button>
              </Stack>
            </InlineAlert>
          )}
          {loadError && (
            <InlineAlert severity="error">
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" gap={1}>
                <span>{loadError}</span>
                <Button size="small" variant="outlined" onClick={reload} sx={outlineBtnSx}>Retry</Button>
              </Stack>
            </InlineAlert>
          )}
        </Stack>
        <Box sx={{ height: 3 }}>
          {loading && <LinearProgress sx={{ height: 3, background: "transparent", "& .MuiLinearProgress-bar": { background: "#ff6a3d" } }} />}
        </Box>
        <TableContainer sx={{ maxHeight: "65vh" }}>
          <Table stickyHeader size="small" aria-label="Supported countries" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={thStyle}>Country</TableCell>
                <TableCell align="center" sx={thStyle}>Active</TableCell>
                <TableCell align="center" sx={thStyle}>Signup</TableCell>
                <TableCell align="center" sx={thStyle}>Onboarding</TableCell>
                <TableCell align="center" sx={thStyle}>State required</TableCell>
                <TableCell sx={thStyle}>Disabled states</TableCell>
                <TableCell align="right" sx={thStyle}>Order</TableCell>
                <TableCell align="right" sx={thStyle}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && !loadError && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8, background: "#1a1a1a", border: "none" }}>
                    <PublicOutlinedIcon sx={{ color: "#2f2f2f", fontSize: 40, mb: 1 }} />
                    <Typography color="#6a6a6a" fontSize={13}>
                      {query ? "No countries match your search." : "No countries configured yet."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && visible.map((c) => (
                <TableRow key={c.alpha2} sx={{ ...trHover, opacity: c.isActive ? 1 : 0.6 }}>
                  <TableCell sx={tdStyle}>
                    <Stack direction="row" gap={1} alignItems="center">
                      <Typography fontSize={13} fontWeight={600} color="#e0e0e0">{c.name}</Typography>
                      <Chip size="small" label={c.alpha2} sx={{ height: 22, color: "#9ca3af", background: "#242424", fontSize: 10 }} />
                    </Stack>
                  </TableCell>
                  <TableCell align="center" sx={tdStyle}>{sw(c, "isActive", "Active")}</TableCell>
                  <TableCell align="center" sx={tdStyle}>{sw(c, "signupEnabled", "Signup", !c.isActive)}</TableCell>
                  <TableCell align="center" sx={tdStyle}>{sw(c, "onboardingEnabled", "Onboarding", !c.isActive)}</TableCell>
                  <TableCell align="center" sx={tdStyle}>
                    {sw(c, "stateRequired", "State required", !c.isActive || !c.hasStates, !c.hasStates ? "This country has no states" : undefined)}
                  </TableCell>
                  <TableCell sx={tdStyle}>
                    {c.hasStates
                      ? (c.disabledStates.length
                        ? <Chip size="small" label={`${c.disabledStates.length} hidden`} sx={{ color: "#f59e0b", background: "#f59e0b18", border: "1px solid #f59e0b40" }} />
                        : <Typography component="span" fontSize={13} color="#6a6a6a">None</Typography>)
                      : <Typography component="span" fontSize={13} color="#6a6a6a">n/a</Typography>}
                  </TableCell>
                  <TableCell align="right" sx={tdStyle}>{c.sortOrder}</TableCell>
                  <TableCell align="right" sx={tdStyle}>
                    <Button size="small" onClick={() => setEditing(c)} disabled={readOnly || !!busy[c.alpha2]} sx={outlineBtnSx}>Edit</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </PushPanel>

      <AddCountryDialog open={addOpen} nextSortOrder={nextSortOrder} onClose={() => setAddOpen(false)}
        onCreate={async (b) => { await create(b); setToast({ msg: "Country added", severity: "success" }); }} />
      <StatesDialog country={editing} onClose={() => setEditing(null)} onSave={(a, p) => savePatch(a, p)} />

      <Dialog
        open={!!confirm}
        onClose={confirming ? undefined : () => setConfirm(null)}
        slotProps={{ paper: { sx: dialogPaperSx } }}
      >
        <DialogTitle sx={dialogTitleSx}>{confirm?.title}</DialogTitle>
        <DialogContent sx={{ background: "#242424", pt: "20px !important" }}>
          <DialogContentText sx={{ color: "#b0b0b0" }}>{confirm?.body}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ background: "#242424", px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(null)} disabled={confirming} sx={{ color: "#8a8a8a" }}>Cancel</Button>
          <Button variant="contained" onClick={runConfirm} disabled={confirming} sx={headerBtnSx}>{confirm?.confirmLabel}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
